import { styled } from "@mui/material/styles";
import {
  Box,
  Button as MuiButton,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import React from "react";
import { DateRangePicker } from "@/components/DatePicker";
import { Label } from "@/components/Label";
import type {
  AcquireBookErrorType,
  AcquireBookRealEstateType,
  AcquireBookStatusFilter,
} from "../types";
import type { CityListResponse } from "@/types/city";

interface PrefectureOption {
  value: string;
  label: string;
}

const FilterSection = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

interface Props {
  filter: AcquireBookStatusFilter;
  realEstateTypeSelectValue: string;
  prefectureSelectValue: string;
  selectedPrefectureId: number;
  selectedCityCode: string;
  prefectureOptions: PrefectureOption[];
  cityList: CityListResponse;
  freeKeywordInput: string;
  errorTypeOptions: AcquireBookErrorType[];
  errorTypeLabels: Record<AcquireBookErrorType, string>;
  realEstateTypeOptions: AcquireBookRealEstateType[];
  realEstateTypeLabels: Record<AcquireBookRealEstateType, string>;
  selectAllValue: string;
  onChangeErrorTypes: (
    event: SelectChangeEvent<AcquireBookStatusFilter["errorTypes"]>
  ) => void;
  onChangeRealEstateTypeSelect: (event: SelectChangeEvent<string>) => void;
  onChangePrefecture: (event: SelectChangeEvent<string>) => void;
  onChangeCity: (event: SelectChangeEvent<string>) => void;
  onChangeLocationKeyword: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeChibanKeyword: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeFreeKeyword: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeAcquiredDateStart: (date: Date | null) => void;
  onChangeAcquiredDateEnd: (date: Date | null) => void;
  onClickSelectLocation: () => void;
  disableSelectLocationButton: boolean;
  onResetFilters: () => void;
}

export const FilterPanel: React.FC<Props> = (props) => {
  const {
    filter,
    realEstateTypeSelectValue,
    prefectureSelectValue,
    selectedPrefectureId,
    selectedCityCode,
    prefectureOptions,
    cityList,
    freeKeywordInput,
    errorTypeOptions,
    errorTypeLabels,
    realEstateTypeOptions,
    realEstateTypeLabels,
    selectAllValue,
    onChangeErrorTypes,
    onChangeRealEstateTypeSelect,
    onChangePrefecture,
    onChangeCity,
    onChangeLocationKeyword,
    onChangeChibanKeyword,
    onChangeFreeKeyword,
    onChangeAcquiredDateStart,
    onChangeAcquiredDateEnd,
    onClickSelectLocation,
    disableSelectLocationButton,
    onResetFilters,
  } = props;

  return (
    <FilterSection>
      <Stack spacing={3}>
        <FormControl sx={{ width: { xs: "100%", sm: 260, md: 280 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            <Label sx={{ mb: 0 }}>エラー種類</Label>
          </Box>
          <Select
            multiple
            value={filter.errorTypes}
            onChange={onChangeErrorTypes}
            input={<OutlinedInput />}
            renderValue={(selected: AcquireBookErrorType[]) =>
              selected.length === errorTypeOptions.length
                ? "全て"
                : selected.map((type) => errorTypeLabels[type]).join("、")
            }
            displayEmpty
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 360,
                },
              },
            }}
          >
            <MenuItem value={selectAllValue}>
              <Checkbox
                checked={filter.errorTypes.length === errorTypeOptions.length}
                indeterminate={
                  filter.errorTypes.length > 0 &&
                  filter.errorTypes.length < errorTypeOptions.length
                }
              />
              <ListItemText primary="全てを選択" />
            </MenuItem>
            {errorTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={filter.errorTypes.includes(type)} />
                <ListItemText primary={errorTypeLabels[type]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: { xs: "100%", sm: 220, md: 240 } }}>
          <Label>種別</Label>
          <Select
            value={realEstateTypeSelectValue}
            onChange={onChangeRealEstateTypeSelect}
            input={<OutlinedInput />}
            displayEmpty
          >
            <MenuItem value="">全て</MenuItem>
            {realEstateTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {realEstateTypeLabels[type]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Label>都道府県</Label>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <FormControl sx={{ width: { xs: "100%", sm: 220, md: 240 } }}>
              <Select
                value={prefectureSelectValue}
                onChange={onChangePrefecture}
                input={<OutlinedInput />}
                displayEmpty
              >
                <MenuItem value="">全て</MenuItem>
                {prefectureOptions.map((prefecture) => (
                  <MenuItem key={prefecture.value} value={prefecture.value}>
                    {prefecture.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MuiButton
              variant="outlined"
              onClick={onClickSelectLocation}
              disabled={disableSelectLocationButton}
              sx={{
                minWidth: 180,
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            >
              所在を選択
            </MuiButton>
          </Stack>
        </Box>

        <FormControl
          disabled={selectedPrefectureId === 0}
          sx={{ width: { xs: "100%", sm: 220, md: 240 } }}
        >
          <Label>市区町村</Label>
          <Select
            value={selectedCityCode}
            onChange={onChangeCity}
            input={<OutlinedInput />}
            displayEmpty
          >
            <MenuItem value="">全て</MenuItem>
            {cityList.list.map((city) => (
              <MenuItem key={city.cityCode} value={city.cityCode}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Box sx={{ flex: 1, minWidth: 260, maxWidth: 480 }}>
            <Label>所在</Label>
            <TextField
              fullWidth
              placeholder="所在地を入力"
              value={filter.locationKeyword}
              onChange={onChangeLocationKeyword}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 260, maxWidth: 480 }}>
            <Label>地番または家屋番号</Label>
            <TextField
              fullWidth
              placeholder="地番や家屋番号で検索"
              value={filter.chibanKeyword}
              onChange={onChangeChibanKeyword}
            />
          </Box>
        </Stack>

        <Box sx={{ width: "100%", maxWidth: 1000 }}>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: { xs: "wrap", md: "nowrap" },
              alignItems: "center",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Label sx={{ mb: 1 }}>取得日</Label>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DateRangePicker
                  startDateLabel="取得日 開始"
                  endDateLabel="取得日 終了"
                  startDate={filter.acquiredDateFrom}
                  endDate={filter.acquiredDateTo}
                  onChangeStartDate={onChangeAcquiredDateStart}
                  onChangeEndDate={onChangeAcquiredDateEnd}
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 470, maxWidth: 800 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <Label sx={{ mb: 0 }}>キーワード検索</Label>
              </Box>
              <TextField
                fullWidth
                value={freeKeywordInput}
                onChange={onChangeFreeKeyword}
                placeholder="所在、地番、家屋番号、都道府県、市区町村で検索できます"
                inputProps={{ maxLength: 100 }}
              />
            </Box>
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <MuiButton variant="outlined" onClick={onResetFilters}>
            条件をクリア
          </MuiButton>
        </Box>
      </Stack>
    </FilterSection>
  );
};
